import { create } from "zustand";
import { devtools } from "zustand/middleware";
import type { Tab } from "./types";

interface PavilionUiState {
  // Current active tab (used for mobile single-panel and desktop selection)
  activeTab: Tab;

  // Desktop: which panels are active (system/tools/portals on left, console/logs/api on right)
  leftPanelTab: Omit<Tab, "console" | "logs" | "api">;
  rightPanelTab: Omit<Tab, "system" | "tools" | "portals">;

  // UI state
  isMobileMenuOpen: boolean;
  isSidebarCollapsed: boolean;

  // Actions
  setActiveTab: (tab: Tab) => void;
  setMobileMenuOpen: (open: boolean) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;

  // Helpers
  toggleMobileMenu: () => void;
  toggleSidebar: () => void;
}

export const usePavilionUiStore = create<PavilionUiState>()(
  devtools(
    (set) => ({
      // State
      activeTab: "system",
      leftPanelTab: "system",
      rightPanelTab: "console",
      isMobileMenuOpen: false,
      isSidebarCollapsed: false,

      // Actions
      setActiveTab: (tab) =>
        set(() => {
          const newState: Partial<PavilionUiState> = { activeTab: tab };

          // Auto-update panel tabs based on the active tab
          if (tab === "system" || tab === "tools" || tab === "portals") {
            newState.leftPanelTab = tab as Omit<
              Tab,
              "console" | "logs" | "api"
            >;
          } else if (tab === "console" || tab === "logs" || tab === "api") {
            newState.rightPanelTab = tab as Omit<
              Tab,
              "system" | "tools" | "portals"
            >;
          }

          return newState;
        }),

      setMobileMenuOpen: (open) => set({ isMobileMenuOpen: open }),
      setSidebarCollapsed: (collapsed) =>
        set({ isSidebarCollapsed: collapsed }),

      // Helpers
      toggleMobileMenu: () =>
        set((state) => ({ isMobileMenuOpen: !state.isMobileMenuOpen })),
      toggleSidebar: () =>
        set((state) => ({ isSidebarCollapsed: !state.isSidebarCollapsed })),
    }),
    {
      name: "pavilion-ui-store",
    },
  ),
);
